import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { t, errorText } from '../i18n/index.ts';
import { api } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { ErrorView, Field, Loading, PlayingCard } from '../components/ui.tsx';

function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="auth">
      <div className="auth-art" aria-hidden="true">
        <div className="cards">
          <PlayingCard card="As" size="l" />
          <PlayingCard card="Kh" size="l" />
        </div>
        <h1>{t('app.name')}</h1>
        <p>{t('auth.privateNote')}</p>
      </div>
      <main className="auth-form" id="main">
        <div className="panel">{children}</div>
      </main>
    </div>
  );
}

function safeNext(next: string | null): string {
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
}

export function Login() {
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const { me } = useSession();
  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [devLink, setDevLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (me?.mfaVerified) return <Navigate to={next} replace />;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ ok: true; devLink?: string }>('/api/auth/link/request', { email });
      // Where to go after signing in, for the page the link opens (same browser).
      try {
        sessionStorage.setItem(NEXT_KEY, next);
      } catch {
        // no storage: the link opens the home page
      }
      setSentTo(email.trim());
      setDevLink(res.devLink ?? null);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  if (sentTo) {
    return (
      <AuthLayout>
        <h2>{t('auth.linkSentTitle')}</h2>
        <p>{t('auth.linkSent', { email: sentTo })}</p>
        <p className="hint">{t('auth.linkSentHelp')}</p>
        {devLink && (
          <div className="alert alert-warn">
            <p style={{ margin: '0 0 8px' }}>{t('auth.devLinkNote')}</p>
            <a className="btn btn-primary btn-block" href={devLink}>
              {t('auth.devLinkOpen')}
            </a>
          </div>
        )}
        <button type="button" className="btn btn-ghost btn-block" style={{ marginTop: 12 }} onClick={() => setSentTo(null)}>
          {t('auth.linkAgain')}
        </button>
      </AuthLayout>
    );
  }
  return (
    <AuthLayout>
      <h2>{t('auth.loginTitle')}</h2>
      <form onSubmit={submit} noValidate>
        {error && (
          <div className="alert alert-error" role="alert">
            {error}
          </div>
        )}
        <Field label={t('auth.email')} htmlFor="email">
          <input id="email" className="input" type="email" autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
        </Field>
        <button className="btn btn-primary btn-block" disabled={busy || !email.includes('@')}>
          {t('auth.sendLink')}
        </button>
      </form>
    </AuthLayout>
  );
}

const NEXT_KEY = 'kotipoker.afterSignIn';

/**
 * The page a sign-in (or e-mail confirmation) link opens. The token is in the URL fragment, which
 * browsers never send to a server, and it is used only by this page's own request: link checkers
 * in mail programs that merely open the page cannot use up the link.
 */
export function LinkSignIn() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const started = useRef(false);
  // Wait for the page's first "who am I" answer: a refresh started while that is still on its way
  // would just return the old signed-out answer.
  const { loading } = useSession();
  useEffect(() => {
    if (started.current || loading) return;
    started.current = true;
    const token = window.location.hash.slice(1);
    // Drop the token from the address bar and history right away.
    window.history.replaceState(null, '', window.location.pathname);
    let next = '/';
    try {
      next = safeNext(sessionStorage.getItem(NEXT_KEY));
      sessionStorage.removeItem(NEXT_KEY);
    } catch {
      // no storage: home page
    }
    if (!token) {
      setError({ code: 'link_invalid' });
      return;
    }
    api
      .post<{ mfa: 'none' | 'verify' | 'setup'; emailChanged: boolean }>('/api/auth/link', { token })
      .then(async (res) => {
        await qc.invalidateQueries({ queryKey: ['me'] });
        if (res.emailChanged) next = '/profiili';
        navigate(res.mfa === 'none' ? next : `/mfa?next=${encodeURIComponent(next)}`, { replace: true });
      })
      .catch(setError);
  }, [navigate, qc, loading]);
  return (
    <AuthLayout>
      {error ? (
        <>
          <h2>{t('auth.linkFailedTitle')}</h2>
          <p>{t('auth.linkFailed')}</p>
          <Link className="btn btn-primary btn-block" to="/kirjaudu">
            {t('auth.newLink')}
          </Link>
        </>
      ) : (
        <>
          <h2>{t('auth.signingIn')}</h2>
          <Loading />
        </>
      )}
    </AuthLayout>
  );
}

export function MfaPage() {
  const { me, loading, refresh } = useSession();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  if (loading) return <Loading />;
  if (!me) return <Navigate to="/kirjaudu" replace />;
  if (me.mfaVerified) return <Navigate to={next} replace />;
  return <AuthLayout>{me.mfaEnabled ? <MfaVerify onDone={refresh} next={next} /> : <MfaSetup onDone={refresh} next={next} />}</AuthLayout>;
}

function MfaVerify({ onDone, next }: { onDone: () => Promise<unknown>; next: string }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/api/auth/mfa', { code });
      await onDone();
      navigate(next, { replace: true });
    } catch (err) {
      setError(errorText(err));
    }
  };
  return (
    <form onSubmit={submit}>
      <h2>{t('auth.mfaTitle')}</h2>
      <p className="hint">{t('auth.mfaHelp')}</p>
      {error && (
        <div className="alert alert-error" role="alert">
          {error}
        </div>
      )}
      <Field label={t('auth.mfaCode')} htmlFor="code">
        <input id="code" className="input" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} />
      </Field>
      <button className="btn btn-primary btn-block">{t('auth.verify')}</button>
    </form>
  );
}

export function MfaSetup({ onDone, next }: { onDone: () => Promise<unknown>; next: string }) {
  const setup = useQuery({ queryKey: ['mfa-setup'], queryFn: () => api.post<{ secret: string; otpauthUrl: string }>('/api/me/mfa/setup'), staleTime: Infinity, retry: false, refetchOnWindowFocus: false, refetchOnReconnect: false });
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (setup.data) void QRCode.toDataURL(setup.data.otpauthUrl, { margin: 0, width: 200 }).then(setQr);
  }, [setup.data]);
  if (codes) {
    return (
      <div>
        <h2>{t('auth.recoveryTitle')}</h2>
        <p>{t('auth.recoveryHelp')}</p>
        <div className="codes" data-testid="recovery-codes">
          {codes.map((c) => (
            <span key={c}>{c}</span>
          ))}
        </div>
        <button
          className="btn btn-primary btn-block"
          onClick={async () => {
            await onDone();
            navigate(next, { replace: true });
          }}
        >
          {t('auth.recoveryDone')}
        </button>
      </div>
    );
  }
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const res = await api.post<{ recoveryCodes: string[] }>('/api/me/mfa/enable', { code });
      setCodes(res.recoveryCodes);
    } catch (err) {
      setError(errorText(err));
    }
  };
  return (
    <form onSubmit={submit}>
      <h2>{t('auth.mfaSetupTitle')}</h2>
      <p className="hint">{t('auth.mfaSetupHelp')}</p>
      {setup.isError && <ErrorView error={setup.error} />}
      {qr && <img className="qr" src={qr} alt="QR" />}
      {setup.data && (
        <p>
          <span className="label">{t('auth.mfaSecret')}: </span>
          <code data-testid="mfa-secret" style={{ overflowWrap: 'anywhere' }}>
            {setup.data.secret}
          </code>
        </p>
      )}
      {error && (
        <div className="alert alert-error" role="alert">
          {error}
        </div>
      )}
      <Field label={t('auth.mfaCode')} htmlFor="code">
        <input id="code" className="input" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} />
      </Field>
      <button className="btn btn-primary btn-block">{t('auth.mfaEnable')}</button>
    </form>
  );
}

/** The invitation link creates the account and signs straight in; no password is ever set. */
export function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const [name, setName] = useState<string | null>(null);
  const started = useRef(false);
  const { loading } = useSession();
  useEffect(() => {
    if (started.current || !token || loading) return;
    started.current = true;
    void (async () => {
      try {
        const invite = await api.get<{ email: string; displayName: string; expiresAt: number }>(`/api/invitations/${token}`);
        setName(invite.displayName);
        const res = await api.post<{ mfa: 'none' | 'setup' }>(`/api/invitations/${token}`);
        await qc.invalidateQueries({ queryKey: ['me'] });
        navigate(res.mfa === 'setup' ? '/mfa' : '/', { replace: true });
      } catch (err) {
        setError(err);
      }
    })();
  }, [token, navigate, qc, loading]);
  return (
    <AuthLayout>
      {error ? (
        <>
          <ErrorView error={error} />
          <p className="hint">{t('auth.inviteFailedHelp')}</p>
          <Link to="/kirjaudu">{t('auth.backToLogin')}</Link>
        </>
      ) : (
        <>
          <h2>{name ? t('auth.inviteTitle', { name }) : t('auth.signingIn')}</h2>
          <Loading />
        </>
      )}
    </AuthLayout>
  );
}
